/**
 * The Renewals list's tab and Expiry Window vocabulary — the UI-facing values
 * shared by the Upcoming/Lapsed tabs and their own Expiry Window Selects, so
 * the page cannot end up with two different ideas of "upcoming" or "lapsed".
 *
 * B9: the list and its badges read the policy-based queue
 * (`GET /renewals/queue/`), whose window is `bucket` (`upcoming|lapsed|all`)
 * plus an optional `days` ceiling (upcoming) or `lapsed_days` floor (lapsed)
 * -- not the old `days_to_expiry` renewal-table param.
 *
 * B1/B3: Upcoming and Lapsed used to be two count badges that both drove ONE
 * combined Expiry Window Select mixing future and historical windows (a
 * "Not yet expired" sat next to "Already lapsed" in the same list). They are
 * now real tabs (`role="tab"`, mutually exclusive, `aria-selected`), and each
 * tab gets its OWN Expiry Window vocabulary and its own translation into the
 * queue's wire shape -- `upcomingWindowToQueueParams` /
 * `lapsedWindowToQueueParams` below -- so a Lapsed-tab value can never be sent
 * while `bucket=upcoming` is in effect, or vice versa.
 *
 * NB `upcoming`/`lapsed` here are NOT the dashboard widget's own upcoming
 * window (`pages/dashboard/upcomingRenewalsQuery.ts`), which is a fixed
 * days-ahead count with its own default, not this page's tabs.
 */

import type { UserRole } from '@/types';

/** Already past expiry — `expiry_date < today` server-side (subject to the
 *  Lapsed tab's own Expiry Window, see `lapsedWindowToQueueParams`). */
export const EXPIRY_WINDOW_LAPSED = 'lapsed';

/** Not yet expired — `expiry_date >= today` server-side (subject to the
 *  Upcoming tab's own Expiry Window, see `upcomingWindowToQueueParams`). */
export const EXPIRY_WINDOW_UPCOMING = 'upcoming';

export type RenewalTab = typeof EXPIRY_WINDOW_LAPSED | typeof EXPIRY_WINDOW_UPCOMING;

/**
 * The two tabs, in the order they render. Kept as a small module (rather than
 * two literals in the page) for the same reason the old badge list was: a tab
 * that silently stopped sending its own bucket would be the #246 hidden-window
 * bug again, just moved from a badge onto a tab.
 */
export const renewalTabs: { id: RenewalTab; label: string }[] = [
  { id: EXPIRY_WINDOW_LAPSED, label: 'Lapsed' },
  { id: EXPIRY_WINDOW_UPCOMING, label: 'Upcoming' },
];

/** The queue's window params for one tab's Expiry Window value. `lapsed_days`
 *  and `archived` are only ever set on the lapsed half; `days` only on the
 *  upcoming half -- a single shape because both feed the same
 *  `RenewalQueueFilters` object. */
export interface RenewalQueueWindowParams {
  bucket: RenewalTab;
  days?: number | 'all';
  lapsed_days?: number;
  archived?: 'all';
}

// ---------------------------------------------------------------------------
// Upcoming tab
// ---------------------------------------------------------------------------

export const upcomingWindowOptions = [
  { value: '', label: 'Not yet expired' },
  { value: '7', label: 'Next 7 days' },
  { value: '15', label: 'Next 15 days' },
  { value: '30', label: 'Next 30 days' },
  { value: '60', label: 'Next 60 days' },
];

/**
 * `''` ("Not yet expired") -> no upper bound at all. `days: 'all'` is
 * REQUIRED, not decoration: when `days` is omitted the backend
 * (`queue.py::parse_window`) applies `RENEWAL_QUEUE_DEFAULT_DAYS` (30), so
 * "Not yet expired" would silently hide a policy expiring in 45 days -- the
 * #246 hidden-window bug again. An unparseable value falls back to the same
 * unbounded window rather than the server's 30-day default.
 */
export function upcomingWindowToQueueParams(window: string | undefined): RenewalQueueWindowParams {
  const value = window ?? '';
  if (value === '') return { bucket: EXPIRY_WINDOW_UPCOMING, days: 'all' };
  const days = Number(value);
  if (Number.isInteger(days) && days >= 0) return { bucket: EXPIRY_WINDOW_UPCOMING, days };
  return { bucket: EXPIRY_WINDOW_UPCOMING, days: 'all' };
}

// ---------------------------------------------------------------------------
// Lapsed tab
// ---------------------------------------------------------------------------

/** Unbounded below, archived rows excluded -- the tab's own default. */
export const LAPSED_WINDOW_ALREADY = '';
export const LAPSED_WINDOW_LAST_30 = '30';
export const LAPSED_WINDOW_LAST_60 = '60';
/** Unbounded below, archived rows INCLUDED -- the whole lapsed book, live and
 *  written off (Board #279's `is_archived`, `apps/policies/models.py`). */
export const LAPSED_WINDOW_ALL = 'all';

/**
 * B3: replaces the single "Already lapsed" entry that used to sit inside the
 * combined Expiry Window list. "Last 30/60 days" and "All Lapsed" did not
 * exist as options anywhere before this -- both map onto query params the
 * backend already parses and tests independently
 * (`apps/renewals/queue.py::parse_window`/`parse_archived`,
 * `backend/tests/test_b9_renewal_queue.py`); `backend/tests/
 * test_b1_renewal_month_year.py::TestLapsedDaysAndArchivedTogether` proves
 * the COMBINATION this page now sends (`lapsed_days` + `archived=all`) still
 * narrows correctly rather than one silently overriding the other.
 */
export const lapsedWindowOptions = [
  { value: LAPSED_WINDOW_ALREADY, label: 'Already lapsed' },
  { value: LAPSED_WINDOW_LAST_30, label: 'Last 30 days' },
  { value: LAPSED_WINDOW_LAST_60, label: 'Last 60 days' },
  { value: LAPSED_WINDOW_ALL, label: 'All Lapsed' },
];

/**
 * The Lapsed options a given ROLE may actually act on.
 *
 * "All Lapsed" is the one option whose entire effect is `archived=all`, and
 * `build_queue` (backend/apps/renewals/queue.py) reads
 * `archived = ARCHIVED_EXCLUDE if customer else parse_archived(params)` -- a
 * customer's `archived` is accepted by the parser and then thrown away, by
 * design (archived rows are a desk tool, not part of a customer's live book).
 * Offering it to a customer would therefore render a control that looks
 * applied and returns a byte-identical list to "Already lapsed": the queue
 * #220 inert-control defect. The backend is right to refuse to widen -- this
 * is the UI declining to offer what it cannot deliver.
 *
 * Scoped here rather than at each call site so the dashboard widget and the
 * Renewals list, both of which a customer reaches, cannot drift apart.
 */
export function lapsedWindowOptionsFor(role: UserRole | undefined) {
  if (role === 'customer') {
    return lapsedWindowOptions.filter((option) => option.value !== LAPSED_WINDOW_ALL);
  }
  return lapsedWindowOptions;
}

export function lapsedWindowToQueueParams(window: string | undefined): RenewalQueueWindowParams {
  const value = window ?? '';
  if (value === LAPSED_WINDOW_LAST_30) return { bucket: EXPIRY_WINDOW_LAPSED, lapsed_days: 30 };
  if (value === LAPSED_WINDOW_LAST_60) return { bucket: EXPIRY_WINDOW_LAPSED, lapsed_days: 60 };
  if (value === LAPSED_WINDOW_ALL) return { bucket: EXPIRY_WINDOW_LAPSED, archived: 'all' };
  return { bucket: EXPIRY_WINDOW_LAPSED };
}

/** Dispatches to the right translation for the active tab. The page's single
 *  source of truth for "which window am I actually asking the server for" --
 *  never build the bucket/days/lapsed_days shape inline at the call site. */
export function windowToQueueParams(tab: RenewalTab, window: string | undefined): RenewalQueueWindowParams {
  return tab === EXPIRY_WINDOW_LAPSED ? lapsedWindowToQueueParams(window) : upcomingWindowToQueueParams(window);
}
